import { useMemo, useEffect, useState } from 'react'
import { Spin, message } from 'antd'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import { formatNb } from '../../utils/utils'
import { getOrderedSelectionsForLive } from '../../utils/selectionUtils'

const titleWrap = {
    width: '88px',
    minHeight: '26px',
    paddingLeft: '5px',
    fontSize: '11px',
    fontWeight: 'bold',
    borderRight: '1px solid #F0F0F0',
    display: 'flex',
    alignItems: 'center',
}

const titleText = {
    fontSize: '11px',
    fontWeight: 'bold',
}

const caseWrap = (stock, isDark, isTextDark) => ({
    width: '113px',
    display: 'flex',
    alignItems: 'center',
    borderRight: '1px solid #F0F0F0',

    color: isTextDark
        ? undefined
        : stock < 6
        ? 'rgba(255, 77, 79, 1)'
        : stock < 16
        ? 'rgba(255, 122, 69, 1)'
        : undefined,

    backgroundColor:
        stock < 6
            ? isDark
                ? 'rgba(255, 225, 223, 1)'
                : 'rgba(255, 241, 240, 1)'
            : stock < 16
            ? isDark
                ? 'rgba(252, 229, 219, 1)'
                : 'rgba(253, 240, 235, 1)'
            : undefined,
})

const caseText = {
    width: '100%',
    fontSize: '11px',
    textAlign: 'center',
}

const lineWrap = {
    display: 'flex',
    flexDirection: 'row',
    fontSize: '11px',
}

const Title = ({ title }) => (
    <div style={titleWrap}>
        <span style={titleText}>{title}</span>
    </div>
)

const getFFPrice = (item) => {
    if (!item) return undefined

    const sold = item?.stock - item?.actualStock
    const dividable = sold - (sold % item?.freeFall?.quantityTrigger)

    const dropedPrice =
        (dividable / item?.freeFall?.quantityTrigger) *
        item?.freeFall?.dropAmount

    if (item?.price - dropedPrice < item?.freeFall?.lowestPrice) {
        return item?.freeFall?.lowestPrice
    }

    return item?.price - dropedPrice
}

const getBouseMinMax = (bourse) => {
    let min = null
    let max = null

    for (let i = 0; i < bourse.length; i++) {
        if (min === null || bourse[i]?.from < min?.from) {
            min = bourse[i]
        }

        if (max === null || bourse[i]?.to > max?.from) {
            max = bourse[i]
        }
    }

    return { max, min }
}

const getBoursePrice = (items, bourse, index) => {
    if (!items) return '-'
    let totalPacks = 0

    for (const item of items) {
        totalPacks += item?.stock - item?.actualStock
    }

    const minmax = getBouseMinMax(bourse)

    if (totalPacks === 0) {
        return minmax?.min?.unitPrice
    }

    if (totalPacks > minmax.max.to) {
        return minmax?.max?.unitPrice
    }

    for (const price of bourse) {
        if (totalPacks >= price?.from && totalPacks <= price?.to) {
            return price?.unitPrice
        }
    }

    return !!index || index === 0 ? items[index]?.price : '-'
}

const PacksStatLive = ({ packs, items, displayItems, freeFall, bourse }) => {
    const { t } = useTranslation()
    const [oldItem, setOldItem] = useState(items)

    const orderedSelections = useMemo(
        () => getOrderedSelectionsForLive(packs, items, displayItems),
        [packs, items, displayItems]
    )

    useEffect(() => {
        const ffMessage = async () => {
            for (let i = 0; i < items.length; i++) {
                const nvPrice = getFFPrice(items[i])

                if (nvPrice !== getFFPrice(oldItem[i])) {
                    message.warning(
                        `add on ${items[i]?.shortName} new price is ${nvPrice?.toFixed(2)}¥`
                    )
                }
            }

            setOldItem(items)
        }

        const bMessage = async () => {
            const nvPrice = getBoursePrice(items, bourse)

            if (nvPrice !== getBoursePrice(oldItem, bourse)) {
                message.warning(`New Bourse price is ${nvPrice?.toFixed(2)}¥`)
            }

            setOldItem(items)
        }

        if (freeFall && oldItem !== items) ffMessage()

        if (!!bourse && oldItem !== items) bMessage()
    }, [items])

    const renderSelectionCell = (
        { entry },
        { isDark, isTextDark, wrapStyle, textStyle, children }
    ) => (
        <div
            key={entry._id}
            style={{
                ...caseWrap(entry.actualStock, isDark, isTextDark),
                ...wrapStyle,
            }}
        >
            <span style={{ ...caseText, ...textStyle }}>{children}</span>
        </div>
    )

    return (
        <div style={{ backgroundColor: 'rgb(255, 255, 255)' }}>
            {!packs && !items ? (
                <div className="spin-loader">
                    <Spin />
                </div>
            ) : (
                <>
                    <div style={lineWrap}>
                        <Title title={t('OFFER_LIVE_ITEMS')} />

                        {orderedSelections.map((selection) =>
                            renderSelectionCell(selection, {
                                children: `${
                                    selection.type === 'pack'
                                        ? t('PACK')
                                        : t('OFFER_LIVE_SINGLE_ITEM')
                                } ${selection.entry.shortName}`,
                            })
                        )}
                    </div>
                    <div
                        style={{
                            ...lineWrap,
                            backgroundColor: '#fafafa',
                        }}
                    >
                        <Title title={t('OFFER_LIVE_NAME')} />

                        {orderedSelections.map((selection) =>
                            renderSelectionCell(selection, {
                                isDark: true,
                                children:
                                    selection.type === 'pack'
                                        ? selection.entry?.name
                                            ? selection.entry.name[
                                                  i18next.language
                                              ]
                                            : ''
                                        : selection.entry.product?.name
                                        ? selection.entry.product?.name[
                                              i18next.language
                                          ]
                                        : '',
                            })
                        )}
                    </div>
                    <div style={lineWrap}>
                        <Title title={t('OFFER_LIVE_QT_AV')} />

                        {orderedSelections.map((selection) =>
                            renderSelectionCell(selection, {
                                children:
                                    selection.entry.actualStock === 0
                                        ? 'Sold out'
                                        : selection.entry.actualStock,
                            })
                        )}
                    </div>
                    <div
                        style={{
                            ...lineWrap,
                            backgroundColor: '#fafafa',
                        }}
                    >
                        <Title title={t('OFFER_LIVE_SOLD')} />

                        {orderedSelections.map((selection) =>
                            renderSelectionCell(selection, {
                                isDark: true,
                                isTextDark: true,
                                children:
                                    selection.entry.stock -
                                    selection.entry.actualStock,
                            })
                        )}
                    </div>
                    <div style={lineWrap}>
                        <Title title={t('OFFER_LIVE_PRICE_CNY')} />

                        {orderedSelections.map((selection) => {
                            const { type, entry } = selection

                            if (type === 'pack') {
                                return renderSelectionCell(selection, {
                                    isTextDark: true,
                                    children: `${formatNb(entry.price)}¥`,
                                })
                            }

                            const itemIndex = items.findIndex(
                                (e) => e._id === entry._id
                            )

                            return renderSelectionCell(selection, {
                                isTextDark: true,
                                wrapStyle: { alignItems: 'start' },
                                textStyle: { padding: '5px 0px' },
                                children: !!bourse ? (
                                    getBoursePrice(items, bourse, itemIndex)
                                ) : freeFall ? (
                                    <>
                                        {entry.stock - entry.actualStock !==
                                            0 && (
                                            <span
                                                style={{
                                                    textDecoration:
                                                        'line-through',
                                                }}
                                            >
                                                {`${formatNb(entry.price)}¥`}
                                                <br />
                                            </span>
                                        )}

                                        {`${formatNb(getFFPrice(entry))}¥`}
                                    </>
                                ) : !!entry?.multipleItem?.length ? (
                                    entry?.multipleItem?.map((b) => (
                                        <>
                                            {`${b.quantity}: ${formatNb(b.unitPrice)}¥`}
                                            <br />
                                        </>
                                    ))
                                ) : (
                                    `${formatNb(entry.price)}¥`
                                ),
                            })
                        })}
                    </div>
                </>
            )}
        </div>
    )
}

export default PacksStatLive
